import {useLivraisonsPossiblesContext, usePromoMpSearch} from 'ff-common';
import {
  Box,
  Button,
  HStack,
  Skeleton,
  Spacer,
  Tag,
  Text,
} from '@chakra-ui/react';
import {CatalogProductsGrid} from '@/domains/catalogue/CatalogProductsGrid';
import {Link, useLocation} from 'react-router-dom';
import {RightSingleArrow} from '@/components/svg/RightSingleArrowIcon';
import {routes} from 'ff-common';
import {useTranslation} from 'react-i18next';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {IconeTemperatureLivraison} from '@/components/IconeTemperatureLivraison';

const ItemSkeleton: React.FC = () => (
  <>
    <HStack
      justifyContent='space-between'
      mx={{base: 5, lg: 6}}
      position='relative'
      mt={{base: '50px', md: '100px'}}
      mb='60px'
      pt='30px'
    >
      <Skeleton
        height='24px'
        width={{base: '200px', md: '350px'}}
      />
      <Skeleton
        height='14px'
        width={{base: '100px', md: '230px'}}
      />
    </HStack>
    <Box m={{base: 5, lg: 6}}>
      <Skeleton
        height='250px'
        width='100%'
      />
    </Box>
  </>
);

export const CatalogueViewAccueil: React.FC = () => {
  const location = useLocation();
  const {t} = useTranslation();
  const {buildClientRoute} = useClientRoute();

  const {effectiveTemperature} = useLivraisonsPossiblesContext();

  const {
    itemResultsPromo,
    itemResultsMP,
    itemResultsMarque,
    marqueAccueil,
    marqueAccueilDispo,
    isLoading,
    promoDispo,
    temperatureTourneeIgnoree,
  } = usePromoMpSearch({temperature_de_livraison: effectiveTemperature});

  const promoLien = buildClientRoute(
    routes.client.catalogue,
    {},
    {nature_du_prix: 'promo', from: 'promo'},
  );
  const MPLien = buildClientRoute(
    routes.client.catalogue,
    {},
    {est_MP: true, from: 'mp'},
  );
  const marqueLien = marqueAccueil
    ? buildClientRoute(
        routes.client.catalogue,
        {},
        {q: marqueAccueil.marque, from: 'accueil'},
      )
    : undefined;

  const title = promoDispo
    ? t('catalogue.promotion_du_moment')
    : t('catalogue.produits_marque_proprietaire');
  const buttonText = t('catalogue.voir_tout');

  // Sélection des hits et du lien selon la priorité : marque d'accueil > promos > MP
  const {selectionHits, lien} =
    marqueAccueilDispo && marqueLien
      ? {selectionHits: itemResultsMarque?.hits, lien: marqueLien}
      : promoDispo
        ? {selectionHits: itemResultsPromo?.hits, lien: promoLien}
        : {selectionHits: itemResultsMP?.hits ?? [], lien: MPLien};

  return (
    <>
      {isLoading && <ItemSkeleton />}
      {/* À partir de lg, 4 px de plus que le titre et la grille : 24 px, comme le bloc d'accueil et « En ce moment » */}
      <Box
        pb={1}
        px={{lg: 1}}
      >
        <Box
          pl={5}
          pr={5}
          position='relative'
          mt={{base: '50px', md: '40px'}}
          mb={3}
        >
          <HStack width='100%'>
            {!isLoading && (
              <>
                <Text
                  fontSize={{base: 'sm', md: 'xl', lg: '2xl'}}
                  fontWeight='700'
                  color='blue.text'
                >
                  {' '}
                  {title}{' '}
                </Text>
                <Spacer />
                <Button
                  rightIcon={<RightSingleArrow />}
                  as={Link}
                  to={lien}
                  color='blue.main'
                  fontWeight='600'
                  variant='ghost'
                  _hover={{backgroundColor: 'transparent'}}
                  fontSize={{base: 'xs', md: 'inherit'}}
                  m={{base: 0, md: 'inherit'}}
                  p={{base: 0, md: 'inherit'}}
                >
                  <Text display={{base: 'block', md: 'none'}}>
                    {t('catalogue.voir_tout')}
                  </Text>
                  <Text display={{base: 'none', md: 'block'}}>
                    {buttonText}
                  </Text>
                </Button>
              </>
            )}
          </HStack>
          {temperatureTourneeIgnoree && effectiveTemperature !== 'les_deux' && (
            <Box p={0}>
              <Tag
                size='md'
                border='solid 1px'
                borderColor='orange.400'
                bg='orange.50'
                color='orange.800'
                p={2}
                gap={2}
              >
                <HStack spacing={2}>
                  <IconeTemperatureLivraison
                    temperature_tournee={effectiveTemperature}
                    boxSize={6}
                  />
                  <Text
                    fontSize='sm'
                    fontWeight='medium'
                  >
                    {t('temperature_livraison.warning_temperature_ignoree')}
                  </Text>
                </HStack>
              </Tag>
            </Box>
          )}
        </Box>
        {selectionHits && (
          <CatalogProductsGrid
            hitResult={selectionHits}
            location={location}
          />
        )}
      </Box>
    </>
  );
};
